(function () {
  'use strict';

  var instances = new WeakMap();

  function hashString(value) {
    var hash = 2166136261;
    for (var index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function seededRandom(seed) {
    var state = seed || 1;
    return function () {
      state += 0x6d2b79f5;
      var value = state;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  }

  function rectanglesOverlap(a, b, gap) {
    return !(
      a.right + gap <= b.left ||
      a.left >= b.right + gap ||
      a.bottom + gap <= b.top ||
      a.top >= b.bottom + gap
    );
  }

  function normalizeAssetUrl(url) {
    if (!url) return '';
    return String(url).split('?')[0].split('#')[0];
  }

  function getIngredientBlockId(ingredient) {
    if (ingredient.id) {
      if (ingredient.id.indexOf('shopify-block-') === 0) {
        return ingredient.id.replace('shopify-block-', '');
      }
      if (ingredient.id.indexOf('block-') === 0) {
        return ingredient.id.replace('block-', '');
      }
      return ingredient.id;
    }
    var blockId = ingredient.getAttribute('data-block-id');
    if (blockId) return blockId;
    return null;
  }

  function getIngredientVisualKey(ingredient) {
    var img = ingredient.querySelector('img');
    var imgSrc = img ? normalizeAssetUrl(img.currentSrc || img.src) : '';
    var productImage = normalizeAssetUrl(ingredient.dataset.productImage || '');
    var fallback = (ingredient.dataset.fallbackAsset || '').trim();
    if (imgSrc) {
      if (fallback && imgSrc.indexOf(fallback) !== -1) {
        return 'fallback:' + fallback;
      }
      return 'image:' + imgSrc;
    }
    if (productImage) return 'product:' + productImage;
    if (fallback) return 'fallback:' + fallback;
    var title = (ingredient.dataset.title || '').trim().toLowerCase();
    if (title) return 'title:' + title;
    return 'block:' + (getIngredientBlockId(ingredient) || 'unknown');
  }

  function partitionUniqueIngredients(ingredients) {
    var seen = Object.create(null);
    var unique = [];
    var duplicates = [];
    ingredients.forEach(function (ingredient) {
      var key = getIngredientVisualKey(ingredient);
      if (seen[key]) {
        duplicates.push({
          key: key,
          blockId: getIngredientBlockId(ingredient),
          ingredient: ingredient
        });
      } else {
        seen[key] = true;
        unique.push(ingredient);
      }
    });
    return { unique: unique, duplicates: duplicates };
  }

  function getViewportTier(viewportWidth) {
    if (viewportWidth <= 720) return 'mobile';
    if (viewportWidth <= 1024) return 'tablet';
    return 'desktop';
  }

  function getZoneSafetyMargin(viewportWidth) {
    if (viewportWidth <= 720) return 16;
    if (viewportWidth <= 1024) return 24;
    return 32;
  }

  function getIngredientGap(viewportWidth, fieldRect) {
    var stageScale = Math.min(fieldRect.width, fieldRect.height) / 760;
    var clampedScale = Math.max(.72, Math.min(1.18, stageScale));
    if (viewportWidth <= 720) return 12 + (8 * clampedScale);
    if (viewportWidth <= 1024) return 18 + (10 * clampedScale);
    return 24 + (12 * clampedScale);
  }

  function getAnimationPadding(viewportWidth) {
    if (viewportWidth <= 720) return 8;
    if (viewportWidth <= 1024) return 10;
    return 12;
  }

  function getUniformIngredientScale(viewportWidth, multiplier, desktopBoost, ingredientCount) {
    var base;
    if (viewportWidth <= 720) base = 0.68;
    else if (viewportWidth <= 1024) base = 0.76;
    else base = 0.8;
    var scale = base * multiplier * desktopBoost;
    var count = Number(ingredientCount || 0);
    if (count >= 9) scale *= viewportWidth <= 720 ? .92 : viewportWidth <= 1024 ? .96 : .98;
    else if (count >= 7) scale *= viewportWidth <= 720 ? .96 : .98;
    else if (count >= 5) scale *= .98;
    if (viewportWidth >= 1025) {
      var viewportHeight = window.innerHeight;
      if (viewportHeight <= 760) scale *= .88;
      else if (viewportHeight <= 860) scale *= .94;
      else if (viewportHeight <= 940) scale *= .97;
    }
    return scale;
  }

  function getUniformMicroIngredientScale(viewportWidth, multiplier) {
    if (viewportWidth <= 320) return 0.28 * multiplier;
    if (viewportWidth <= 360) return 0.3 * multiplier;
    if (viewportWidth <= 430) return 0.32 * multiplier;
    if (viewportWidth <= 480) return 0.34 * multiplier;
    if (viewportWidth <= 767) return 0.36 * multiplier;
    return 0.38 * multiplier;
  }

  function getIngredientVisibleScaleFloor(viewportWidth, multiplier) {
    if (viewportWidth <= 320) return 0.4 * multiplier;
    if (viewportWidth <= 430) return 0.42 * multiplier;
    if (viewportWidth <= 767) return 0.44 * multiplier;
    if (viewportWidth <= 1024) return 0.48 * multiplier;
    return 0.56 * multiplier;
  }

  function expandRect(rect, margin) {
    return {
      left: rect.left - margin,
      top: rect.top - margin,
      right: rect.right + margin,
      bottom: rect.bottom + margin
    };
  }

  function getRotatedHalfExtents(width, height, scale, rotateDeg) {
    var w = width * scale;
    var h = height * scale;
    var rad = rotateDeg * Math.PI / 180;
    var cos = Math.abs(Math.cos(rad));
    var sin = Math.abs(Math.sin(rad));
    return {
      halfW: (w * cos + h * sin) / 2,
      halfH: (w * sin + h * cos) / 2
    };
  }

  function readIngredientMeta(ingredient) {
    var fallbackUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.all_products_collection_url) || '/collections/all';
    if (!ingredient) {
      return {
        image: '',
        name: 'Featured ingredient',
        description: 'Explore premium Morganics pantry essentials.',
        productUrl: fallbackUrl,
        ctaLabel: 'Shop ingredients',
        hasLinkedProduct: false
      };
    }

    var imgEl = ingredient.querySelector('img');
    var visibleImage = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';
    var image = ingredient.dataset.ingredientImage || visibleImage || ingredient.dataset.productImage || '';
    var name = ingredient.dataset.title || ingredient.dataset.productTitle || '';
    if (!name && ingredient.getAttribute('aria-label')) {
      name = ingredient.getAttribute('aria-label').replace(/^View\s+/i, '').trim();
    }
    if (!name) name = 'Featured ingredient';

    var description = ingredient.dataset.description || ingredient.dataset.ingredientDescription || '';
    description = description.replace(/\s+/g, ' ').trim();
    if (!description) {
      description = 'Discover premium ' + name + ' from Morganics.';
    }

    var productUrl = ingredient.dataset.productUrl || ingredient.dataset.link || '';
    if (!productUrl || productUrl === '#') {
      productUrl = fallbackUrl;
    }

    var hasLinkedProduct = Boolean(
      ingredient.dataset.productHandle ||
      (productUrl.indexOf('/products/') !== -1)
    );

    return {
      image: image,
      name: name,
      description: description,
      productUrl: productUrl,
      ctaLabel: hasLinkedProduct ? 'View product' : 'Shop ingredients',
      hasLinkedProduct: hasLinkedProduct
    };
  }

  var desktopPreviewQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)');

  function readConfig(root) {
    var node = root.querySelector('[data-hero-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('Morganics hero configuration could not be parsed.', error);
      return {};
    }
  }

  function MorganicsHeroSlider(root) {
    this.root = root;
    this.config = readConfig(root);
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-mhs-slide]'));
    this.navCards = Array.prototype.slice.call(root.querySelectorAll('[data-mhs-nav]'));
    this.currentIndex = Math.max(0, this.slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    }));
    this.timer = null;
    this.frame = null;
    this.paused = false;
    this.spotlightOpen = false;
    this.destroyController = new AbortController();
    this.signal = this.destroyController.signal;
    this.prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.resizeObserver = null;
    this.duplicateReportCache = Object.create(null);

    if (!this.slides.length) return;
    this.root.dataset.motionReduced = String(Boolean(this.prefersReducedMotion || this.config.reduceMotion));
    this.root.dataset.floatingIngredients = String(this.config.floatingIngredients !== false);
    this.root.dataset.floatingAnimation = String(this.config.floatingAnimation !== false);
    this.root.dataset.burstGlow = String(this.config.burstGlow !== false);
    this.bindControls();
    this.bindPauseBehavior();
    this.bindIngredients();
    this.bindParallax();
    this.bindPlacementRecalc();
    this.bindShopifyEditor();
    this.observeStage();
    this.activate(this.currentIndex, false);
  }

  MorganicsHeroSlider.prototype.bindControls = function () {
    var self = this;
    this.navCards.forEach(function (card, index) {
      card.addEventListener('click', function () {
        self.activate(index, true);
      }, { signal: self.signal });
    });

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft' && event.target.closest('.mhs-slide-nav')) self.activate(self.currentIndex - 1, true);
      if (event.key === 'ArrowRight' && event.target.closest('.mhs-slide-nav')) self.activate(self.currentIndex + 1, true);
      if (event.key === 'Escape') self.closeSpotlight();
    }, { signal: this.signal });
  };

  MorganicsHeroSlider.prototype.bindPauseBehavior = function () {
    var self = this;
    if (this.config.pauseOnHover) {
      this.root.addEventListener('mouseenter', function () {
        self.pause();
      }, { signal: this.signal });
      this.root.addEventListener('mouseleave', function () {
        self.resume();
      }, { signal: this.signal });
    }

    if (this.config.pauseOnHover || this.config.pauseOnFocus) {
      this.root.addEventListener('focusin', function () {
        self.pause();
      }, { signal: this.signal });
      this.root.addEventListener('focusout', function (event) {
        if (!self.root.contains(event.relatedTarget)) self.resume();
      }, { signal: this.signal });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    }, { signal: this.signal });
  };

  MorganicsHeroSlider.prototype.bindIngredients = function () {
    var self = this;
    this.root.querySelectorAll('[data-mhs-ingredient]').forEach(function (ingredient) {
      ingredient.addEventListener('click', function (event) {
        if (desktopPreviewQuery.matches) return;
        event.preventDefault();
        self.openSpotlight(ingredient);
      }, { signal: self.signal });
    });

    var close = this.root.querySelector('[data-spotlight-close]');
    if (close) {
      close.addEventListener('click', function () {
        self.closeSpotlight();
      }, { signal: this.signal });
    }

    document.addEventListener('pointerdown', function (event) {
      if (!self.spotlightOpen) return;
      if (event.target.closest('[data-mhs-spotlight]') || event.target.closest('[data-mhs-ingredient]')) return;
      self.closeSpotlight();
    }, { signal: this.signal });
  };

  MorganicsHeroSlider.prototype.bindParallax = function () {
    var self = this;
    if (!this.config.parallax || this.prefersReducedMotion || this.config.reduceMotion) return;

    this.root.addEventListener('pointermove', function (event) {
      if (window.innerWidth < 1025) return;
      var rect = self.root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = ((event.clientX - rect.left) / rect.width) - .5;
      var y = ((event.clientY - rect.top) / rect.height) - .5;
      self.root.style.setProperty('--stage-parallax-x', (x * 10).toFixed(2) + 'px');
      self.root.style.setProperty('--stage-parallax-y', (y * 7).toFixed(2) + 'px');
      self.root.style.setProperty('--ingredient-parallax-x', (x * 7).toFixed(2) + 'px');
      self.root.style.setProperty('--ingredient-parallax-y', (y * 5).toFixed(2) + 'px');
    }, { signal: this.signal, passive: true });

    this.root.addEventListener('pointerleave', function () {
      self.root.style.setProperty('--stage-parallax-x', '0px');
      self.root.style.setProperty('--stage-parallax-y', '0px');
      self.root.style.setProperty('--ingredient-parallax-x', '0px');
      self.root.style.setProperty('--ingredient-parallax-y', '0px');
    }, { signal: this.signal, passive: true });
  };

  MorganicsHeroSlider.prototype.bindPlacementRecalc = function () {
    var self = this;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        self.schedulePlacement();
      }).catch(function () {});
    }

    this.root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        self.schedulePlacement();
      }, { once: true, signal: self.signal });
    });
  };

  MorganicsHeroSlider.prototype.bindShopifyEditor = function () {
    var self = this;
    document.addEventListener('shopify:block:select', function (event) {
      if (!self.root.contains(event.target)) return;
      var slide = event.target.closest('[data-mhs-slide]');
      if (!slide && event.detail && event.detail.blockId) {
        slide = self.root.querySelector('[data-block-id="' + event.detail.blockId + '"]');
      }
      if (slide) self.activate(Number(slide.dataset.slideIndex), false);
      self.pause();
    }, { signal: this.signal });

    document.addEventListener('shopify:block:deselect', function (event) {
      if (self.root.contains(event.target)) self.resume();
    }, { signal: this.signal });
  };

  MorganicsHeroSlider.prototype.observeStage = function () {
    var self = this;
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.schedulePlacement();
      });
      this.slides.forEach(function (slide) {
        var stage = slide.querySelector('[data-mhs-stage]');
        if (stage) self.resizeObserver.observe(stage);
      });
    }

    window.addEventListener('resize', function () {
      self.schedulePlacement();
    }, { signal: this.signal, passive: true });
    window.addEventListener('orientationchange', function () {
      self.schedulePlacement();
    }, { signal: this.signal, passive: true });
  };

  MorganicsHeroSlider.prototype.activate = function (index, userInitiated) {
    if (!this.slides.length) return;
    var normalized = (index + this.slides.length) % this.slides.length;
    this.closeSpotlight();
    this.currentIndex = normalized;

    this.slides.forEach(function (slide, slideIndex) {
      var active = slideIndex === normalized;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach(function (control) {
        if (active) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', '-1');
      });
    });

    this.navCards.forEach(function (card, cardIndex) {
      var active = cardIndex === normalized;
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.schedulePlacement();
    if (userInitiated) this.restartAutoplay();
    else this.startAutoplay();
  };

  MorganicsHeroSlider.prototype.schedulePlacement = function () {
    var self = this;
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = window.requestAnimationFrame(function () {
      self.frame = null;
      self.placeIngredients(self.slides[self.currentIndex]);
    });
  };

  MorganicsHeroSlider.prototype.placeIngredients = function (slide) {
    if (!slide || !slide.classList.contains('is-active')) return;
    if (this.config.floatingIngredients === false) return;

    var field = slide.querySelector('[data-floating-field]');
    var stack = slide.querySelector('[data-product-stack]');
    if (!field || !stack) return;

    var fieldRect = field.getBoundingClientRect();
    if (!fieldRect.width || !fieldRect.height) return;

    var stone = slide.querySelector('.mhs-stone');
    var stoneRect = stone ? stone.getBoundingClientRect() : null;
    var stoneLimit = stoneRect ? stoneRect.bottom - fieldRect.top : fieldRect.height;

    var allIngredients = Array.prototype.slice.call(field.querySelectorAll('[data-mhs-ingredient]'));
    allIngredients.forEach(function (ingredient) {
      ingredient.classList.remove('is-placed', 'is-suppressed', 'is-compact', 'is-duplicate-config', 'is-micro');
      ingredient.hidden = false;
      ingredient.removeAttribute('aria-hidden');
      ingredient.style.removeProperty('z-index');
    });

    var partition = partitionUniqueIngredients(allIngredients);
    partition.duplicates.forEach(function (duplicate) {
      duplicate.ingredient.classList.add('is-duplicate-config');
      duplicate.ingredient.hidden = true;
      duplicate.ingredient.setAttribute('aria-hidden', 'true');
    });

    if (partition.duplicates.length) {
      var slideKey = String(slide.dataset.slideHandle || slide.dataset.slideIndex || this.currentIndex);
      var duplicateIds = partition.duplicates.map(function (duplicate) {
        return duplicate.blockId || duplicate.key;
      });
      var duplicateSignature = duplicateIds.join(',');
      if (this.duplicateReportCache[slideKey] !== duplicateSignature) {
        console.warn(
          'Morganics hero: duplicate ingredient configuration on slide "' + slideKey + '". Keeping the first instance only. Duplicate block IDs:',
          duplicateIds
        );
        this.duplicateReportCache[slideKey] = duplicateSignature;
      }
    }

    var ingredients = partition.unique.slice().sort(function (a, b) {
      return Number(b.dataset.priority || 0) - Number(a.dataset.priority || 0);
    });

    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;
    var viewportTier = getViewportTier(viewportWidth);
    var portraitViewport = viewportHeight >= viewportWidth;
    var zoneSafetyMargin = getZoneSafetyMargin(viewportWidth);
    var ingredientGap = getIngredientGap(viewportWidth, fieldRect);
    var animationPadding = getAnimationPadding(viewportWidth);
    var desktopScaleBoost = viewportWidth >= 1025 ? 1.2 : 1;
    var scaleMultiplier = Number(this.config.ingredientScale || 1);
    var uniformScale = getUniformIngredientScale(viewportWidth, scaleMultiplier, desktopScaleBoost, ingredients.length);
    var uniformMicroScale = getUniformMicroIngredientScale(viewportWidth, scaleMultiplier);
    var spread = Number(this.config.ingredientSpread || 1);
    var jitterEnabled = this.config.ingredientJitter !== false;
    var seedBase = String(this.config.sectionId || '') + ':' + String(slide.dataset.slideHandle || '');

    var zones = viewportWidth <= 720 ? [
      { x: 50, y: 9, rotate: -8, depth: .34, blur: .30 },
      { x: 24, y: 25, rotate: 13, depth: .68, blur: .08 },
      { x: 76, y: 25, rotate: -12, depth: .64, blur: .10 },
      { x: 33, y: 48, rotate: -16, depth: .42, blur: .28 },
      { x: 68, y: 46, rotate: 14, depth: .40, blur: .30 },
      { x: 12, y: 18, rotate: -20, depth: .30, blur: .36 },
      { x: 88, y: 20, rotate: 18, depth: .28, blur: .38 },
      { x: 18, y: 62, rotate: 12, depth: .24, blur: .42 },
      { x: 82, y: 64, rotate: -14, depth: .22, blur: .44 },
      { x: 50, y: 72, rotate: 6, depth: .20, blur: .48 },
      { x: 30, y: 12, rotate: -10, depth: .18, blur: .50 },
      { x: 70, y: 14, rotate: 10, depth: .16, blur: .52 }
    ] : viewportWidth <= 1024 ? [
      { x: 50, y: 5, rotate: -5, depth: .26, blur: .46 },
      { x: 34, y: 11, rotate: 13, depth: .50, blur: .20 },
      { x: 66, y: 12, rotate: -12, depth: .48, blur: .22 },
      { x: 20, y: 27, rotate: -17, depth: .74, blur: .05 },
      { x: 80, y: 27, rotate: 16, depth: .70, blur: .06 },
      { x: 24, y: 49, rotate: 11, depth: .42, blur: .30 },
      { x: 76, y: 50, rotate: -11, depth: .40, blur: .30 },
      { x: 50, y: 66, rotate: 6, depth: .22, blur: .56 },
      { x: 14, y: 16, rotate: -22, depth: .28, blur: .40 },
      { x: 86, y: 18, rotate: 20, depth: .26, blur: .42 },
      { x: 12, y: 58, rotate: 14, depth: .20, blur: .48 },
      { x: 88, y: 60, rotate: -16, depth: .18, blur: .50 }
    ] : [
      { x: 18, y: 16, rotate: -25, depth: .52, blur: .18 },
      { x: 36, y: 8, rotate: 18, depth: .70, blur: .04 },
      { x: 52, y: 10, rotate: -12, depth: .36, blur: .34 },
      { x: 68, y: 12, rotate: 28, depth: .66, blur: .06 },
      { x: 78, y: 25, rotate: -18, depth: .48, blur: .20 },
      { x: 24, y: 38, rotate: 20, depth: .44, blur: .24 },
      { x: 74, y: 40, rotate: -22, depth: .72, blur: .04 },
      { x: 18, y: 55, rotate: -35, depth: .40, blur: .28 },
      { x: 82, y: 58, rotate: 32, depth: .64, blur: .08 },
      { x: 28, y: 70, rotate: 15, depth: .28, blur: .44 },
      { x: 42, y: 76, rotate: -10, depth: .20, blur: .58 },
      { x: 62, y: 72, rotate: 20, depth: .32, blur: .38 },
      { x: 76, y: 68, rotate: -18, depth: .24, blur: .50 }
    ];

    function clampedPercent(value) {
      return Math.max(0, Math.min(100, value));
    }

    function clampIngredientY(value, height) {
      var bottomClearance = viewportWidth <= 720 ? 12 : viewportWidth <= 1024 ? 14 : 18;
      var maximumY = Math.min(fieldRect.height - height, stoneLimit - height - bottomClearance);
      return Math.max(0, Math.min(maximumY, value));
    }

    function rectToFieldBox(rect) {
      return {
        left: rect.left - fieldRect.left,
        top: rect.top - fieldRect.top,
        right: rect.right - fieldRect.left,
        bottom: rect.bottom - fieldRect.top
      };
    }

    function addForbiddenNode(node, margin) {
      if (!node) return;
      var rect = node.getBoundingClientRect();
      if (!rect.width && !rect.height) return;
      var box = expandRect(rectToFieldBox(rect), margin);
      if (box.right < 0 || box.bottom < 0 || box.left > fieldRect.width || box.top > fieldRect.height) return;
      forbiddenRects.push(box);
    }

    function addBrandingCoreZone(node, widthRatio, heightRatio, margin) {
      if (!node) return;
      var rect = node.getBoundingClientRect();
      if (!rect.width && !rect.height) return;
      var box = rectToFieldBox(rect);
      var coreW = (box.right - box.left) * widthRatio;
      var coreH = (box.bottom - box.top) * heightRatio;
      var centerX = (box.left + box.right) / 2;
      var centerY = (box.top + box.bottom) / 2;
      forbiddenRects.push({
        left: centerX - (coreW / 2) - margin,
        top: centerY - (coreH / 2) - margin,
        right: centerX + (coreW / 2) + margin,
        bottom: centerY + (coreH / 2) + margin
      });
    }

    var forbiddenRects = [];

    var shortDesktopViewport = viewportWidth >= 1025 && viewportHeight <= 820;

    slide.querySelectorAll('.mhs-pouch').forEach(function (node) {
      addBrandingCoreZone(
        node,
        viewportWidth <= 720 ? .34 : .42,
        viewportWidth <= 720 ? .28 : .36,
        zoneSafetyMargin + 4
      );
    });
    slide.querySelectorAll('.mhs-pile').forEach(function (node) {
      addBrandingCoreZone(
        node,
        viewportWidth <= 720 ? .62 : .72,
        viewportWidth <= 720 ? .36 : .48,
        viewportWidth <= 720 ? 10 : Math.max(10, zoneSafetyMargin - 10)
      );
    });

    slide.querySelectorAll('.mhs-copy, .mhs-title, .mhs-description, .mhs-ctas, .mhs-cta, .mhs-tagline').forEach(function (node) {
      addForbiddenNode(node, zoneSafetyMargin);
    });

    var navNode = this.root.querySelector('.mhs-slide-nav');
    if (navNode) addForbiddenNode(navNode, zoneSafetyMargin);

    var spotlightNode = this.root.querySelector('[data-mhs-spotlight]');
    if (spotlightNode && spotlightNode.classList.contains('is-visible')) {
      addForbiddenNode(spotlightNode, zoneSafetyMargin);
    }

    var headerNode = document.querySelector('[data-morganics-header]');
    if (headerNode) {
      var headerRect = headerNode.getBoundingClientRect();
      var headerBox = rectToFieldBox(headerRect);
      if (headerBox.bottom > 0) {
        forbiddenRects.push({
          left: 0,
          top: Math.min(0, headerBox.top) - zoneSafetyMargin,
          right: fieldRect.width,
          bottom: headerBox.bottom + zoneSafetyMargin
        });
      }
    }

    function scatterFromOrigin(index, random, zone) {
      var originX = 50;
      var originY = viewportWidth <= 720 ? 30 : viewportWidth <= 1024 ? 32 : 34;
      var baseAngles = viewportWidth <= 720
        ? [-92, -152, -28, 160, 20, -118, 118, -64, 64, -172, 172, 42]
        : viewportWidth <= 1024
          ? [-94, -130, -56, -164, -18, 145, 35, -88, -110, 110, -70, 70]
          : [-115, -90, -65, -155, -25, 145, 35, 178, 2, 125, 55, -135, -45];
      var baseRadii = viewportWidth <= 720
        ? [23, 32, 32, 35, 35, 28, 28, 30, 30, 26, 26, 24]
        : viewportWidth <= 1024
          ? [30, 38, 38, 51, 51, 54, 54, 36, 32, 32, 28, 28]
          : [38, 36, 40, 58, 58, 56, 56, 64, 64, 72, 72, 50, 50];
      var angle = baseAngles[index % baseAngles.length] + (jitterEnabled ? ((random() - .5) * 18) : 0);
      var radius = baseRadii[index % baseRadii.length] * spread * (jitterEnabled ? (.92 + random() * .16) : 1);
      var radians = angle * Math.PI / 180;
      return {
        originX: originX,
        originY: originY,
        angle: angle,
        radius: radius,
        xPercent: clampedPercent(originX + Math.cos(radians) * radius),
        yPercent: clampedPercent(originY + Math.sin(radians) * radius * .72),
        rotate: zone.rotate,
        depth: zone.depth,
        blur: zone.blur
      };
    }

    function buildCompactSlots(count) {
      var slots = [];
      var rings = viewportWidth <= 720 ? 3 : viewportWidth <= 1024 ? 4 : 5;
      for (var ring = 0; ring < rings; ring += 1) {
        var ringCount = Math.max(5, Math.ceil(count / rings) + 1);
        var radius = viewportWidth <= 720 ? (18 + ring * 10) : viewportWidth <= 1024 ? (20 + ring * 11) : (22 + ring * 12);
        for (var slot = 0; slot < ringCount; slot += 1) {
          var angle = -90 + ((360 / ringCount) * slot) + (ring * 8);
          var radians = angle * Math.PI / 180;
          slots.push({
            xPercent: clampedPercent(50 + Math.cos(radians) * radius),
            yPercent: clampedPercent((viewportWidth <= 720 ? 30 : 32) + Math.sin(radians) * radius * .68),
            rotate: angle * .22,
            depth: Math.max(.14, .42 - ring * .1),
            blur: Math.min(.56, .18 + ring * .12)
          });
        }
      }
      return slots;
    }

    function buildEdgeSlots() {
      return viewportWidth <= 720
        ? [{ x: 8, y: 18 }, { x: 92, y: 18 }, { x: 8, y: 76 }, { x: 92, y: 76 }, { x: 50, y: 10 }, { x: 50, y: 80 }, { x: 22, y: 12 }, { x: 78, y: 12 }]
        : [{ x: 6, y: 14 }, { x: 94, y: 14 }, { x: 6, y: 80 }, { x: 94, y: 80 }, { x: 50, y: 8 }, { x: 50, y: 84 }, { x: 20, y: 10 }, { x: 80, y: 10 }, { x: 16, y: 44 }, { x: 84, y: 44 }];
    }

    function buildVisualBox(candidatePoint, width, height, scale) {
      var rawX = (candidatePoint.xPercent / 100 * fieldRect.width) - (width / 2);
      var rawY = (candidatePoint.yPercent / 100 * fieldRect.height) - (height / 2);
      var clampedX = Math.max(0, Math.min(fieldRect.width - width, rawX));
      var clampedY = clampIngredientY(rawY, height);
      var rotate = candidatePoint.rotate || 0;
      var blurPx = Number(candidatePoint.blur || 0);
      var scaledW = width * scale;
      var scaledH = height * scale;
      var positionLeft = clampedX + ((width - scaledW) / 2);
      var positionTop = clampedY + ((height - scaledH) / 2);
      var positionRight = positionLeft + scaledW;
      var positionBottom = positionTop + scaledH;
      var extents = getRotatedHalfExtents(width, height, scale, rotate);
      var motionPad = viewportWidth <= 360
        ? Math.max(2, (animationPadding * .22) + (blurPx * .45))
        : viewportWidth <= 480
          ? Math.max(2, (animationPadding * .32) + (blurPx * .55))
          : ((animationPadding * .65) + Math.max(3, blurPx * 1.1));
      var centerX = clampedX + (width / 2);
      var centerY = clampedY + (height / 2);
      return {
        x: clampedX,
        y: clampedY,
        positionLeft: positionLeft,
        positionTop: positionTop,
        positionRight: positionRight,
        positionBottom: positionBottom,
        left: centerX - extents.halfW - motionPad,
        top: centerY - extents.halfH - motionPad,
        right: centerX + extents.halfW + motionPad,
        bottom: centerY + extents.halfH + motionPad,
        xPercent: candidatePoint.xPercent,
        yPercent: candidatePoint.yPercent,
        scale: scale,
        rotate: rotate,
        depth: candidatePoint.depth,
        blur: blurPx,
        angle: candidatePoint.angle || 0,
        radius: candidatePoint.radius || 0,
        originX: candidatePoint.originX || 50,
        originY: candidatePoint.originY || 32
      };
    }

    function boxesTouch(a, b, gap) {
      return rectanglesOverlap(a, b, gap);
    }

    function overlapsForbidden(box) {
      return forbiddenRects.some(function (rect) {
        return boxesTouch(box, rect, 0);
      });
    }

    function isValidPlacement(box, gapOverride) {
      var gap = gapOverride == null ? ingredientGap : gapOverride;
      if (
        box.positionLeft < -8 ||
        box.positionTop < -8 ||
        box.positionRight > fieldRect.width + 8 ||
        box.positionBottom > fieldRect.height + 8
      ) {
        return false;
      }
      if (overlapsForbidden(box)) return false;
      return !placedBoxes.some(function (placedBox) {
        return boxesTouch(box, placedBox, gap);
      });
    }

    function applyPlacement(ingredient, box, ingredientIndex, random) {
      ingredient.style.setProperty('--x', box.x.toFixed(1) + 'px');
      ingredient.style.setProperty('--y', box.y.toFixed(1) + 'px');
      ingredient.style.setProperty('--ingredient-x', box.x.toFixed(1) + 'px');
      ingredient.style.setProperty('--ingredient-y', box.y.toFixed(1) + 'px');
      ingredient.style.setProperty('--scale', box.scale.toFixed(2));
      ingredient.style.setProperty('--ingredient-scale', box.scale.toFixed(2));
      ingredient.style.setProperty('--rotate', box.rotate.toFixed(1) + 'deg');
      ingredient.style.setProperty('--ingredient-rotation', box.rotate.toFixed(1) + 'deg');
      ingredient.style.setProperty('--delay', (-ingredientIndex * .52).toFixed(2) + 's');
      ingredient.style.setProperty('--duration', (5 + random() * 4).toFixed(2) + 's');
      ingredient.style.setProperty('--depth', Number(box.depth || .4).toFixed(2));
      ingredient.style.setProperty('--depth-blur', Number(box.blur || 0).toFixed(2) + 'px');
      ingredient.style.setProperty('--float-x', (((random() - .5) * 4) + ((box.xPercent - 50) * .03)).toFixed(1) + 'px');
      ingredient.style.setProperty('--float-y', (((random() - .5) * 4) - (box.depth || .4) * 2.5).toFixed(1) + 'px');
      ingredient.style.setProperty('--float-rotate', (((random() - .5) * 4) + (box.rotate > 0 ? .8 : -.8)).toFixed(1) + 'deg');
      ingredient.style.setProperty('--float-duration', (8.4 + random() * 3.2).toFixed(2) + 's');
      ingredient.style.zIndex = String(Math.round(20 + box.depth * 32));
      ingredient.style.setProperty('--z', String(Math.round(20 + box.depth * 32)));
      ingredient.classList.add('is-placed');
      placedBoxes.push(box);
    }

    function clonePoint(point) {
      return {
        originX: point.originX,
        originY: point.originY,
        angle: point.angle,
        radius: point.radius,
        xPercent: point.xPercent,
        yPercent: point.yPercent,
        rotate: point.rotate,
        depth: point.depth,
        blur: point.blur
      };
    }

    function findPlacement(point, width, height, targetScale, random, ingredientIndex) {
      var scaleFactors = desktopScaleBoost > 1
        ? [1, .97, .94, .91, .88, .85, .82, .78, .74, .70, .66, .62, .58, .54, .50]
        : [1, .95, .9, .85, .8, .75, .7, .65, .6, .55, .5, .46, .42];
      var compactSlots = buildCompactSlots(ingredients.length);
      var edgeSlots = buildEdgeSlots();

      function tryCandidate(candidatePoint, scale, compact, micro) {
        var candidateBox = buildVisualBox(candidatePoint, width, height, scale);
        if (isValidPlacement(candidateBox)) {
          return { box: candidateBox, point: candidatePoint, compact: compact, micro: Boolean(micro) };
        }
        return null;
      }

      for (var scaleIndex = 0; scaleIndex < scaleFactors.length; scaleIndex += 1) {
        var scale = targetScale * scaleFactors[scaleIndex];
        var attemptPoint = clonePoint(point);

        for (var spacingAttempt = 0; spacingAttempt < 36; spacingAttempt += 1) {
          var direction = spacingAttempt % 2 === 0 ? 1 : -1;
          var adjustedAngle = attemptPoint.angle + (direction * (8 + spacingAttempt * 2.5));
          var adjustedRadius = attemptPoint.radius * (1.02 + spacingAttempt * .015);
          var adjustedRadians = adjustedAngle * Math.PI / 180;
          attemptPoint.xPercent = clampedPercent(attemptPoint.originX + Math.cos(adjustedRadians) * adjustedRadius);
          attemptPoint.yPercent = clampedPercent(attemptPoint.originY + Math.sin(adjustedRadians) * adjustedRadius * .72);
          attemptPoint.angle = adjustedAngle;
          attemptPoint.radius = adjustedRadius;
          var primaryPlacement = tryCandidate(clonePoint(attemptPoint), scale, scaleFactors[scaleIndex] < 1, false);
          if (primaryPlacement) return primaryPlacement;
        }

        for (var slotIndex = 0; slotIndex < compactSlots.length; slotIndex += 1) {
          var slot = compactSlots[slotIndex];
          var compactPoint = {
            xPercent: slot.xPercent,
            yPercent: slot.yPercent,
            rotate: slot.rotate + (jitterEnabled ? ((random() - .5) * 10) : 0),
            depth: slot.depth,
            blur: slot.blur,
            angle: -90 + (slotIndex * 17),
            radius: 20 + (slotIndex % 5) * 8,
            originX: 50,
            originY: viewportWidth <= 720 ? 30 : 32
          };
          var compactPlacement = tryCandidate(compactPoint, scale, true, false);
          if (compactPlacement) return compactPlacement;
        }

        for (var edgeIndex = 0; edgeIndex < edgeSlots.length; edgeIndex += 1) {
          var edge = edgeSlots[edgeIndex];
          var edgePoint = {
            xPercent: edge.x,
            yPercent: edge.y,
            rotate: (edgeIndex % 2 === 0 ? -8 : 8),
            depth: .16,
            blur: .48,
            angle: 0,
            radius: 0,
            originX: edge.x,
            originY: edge.y
          };
          var edgePlacement = tryCandidate(edgePoint, scale, true, false);
          if (edgePlacement) return edgePlacement;
        }
      }

      for (var sweepY = 4; sweepY <= 92; sweepY += 4) {
        for (var sweepX = 4; sweepX <= 94; sweepX += 4) {
          for (var sweepScaleIndex = 0; sweepScaleIndex < 18; sweepScaleIndex += 1) {
            var sweepScale = targetScale * (.88 - sweepScaleIndex * .04);
            if (sweepScale < .1) break;
            var sweepPoint = {
              xPercent: sweepX,
              yPercent: sweepY,
              rotate: point.rotate,
              depth: point.depth,
              blur: point.blur,
              angle: 0,
              radius: 0,
              originX: sweepX,
              originY: sweepY
            };
            var sweepPlacement = tryCandidate(sweepPoint, sweepScale, true, false);
            if (sweepPlacement) return sweepPlacement;
          }
        }
      }

      if (viewportWidth <= 720) {
        var microOriginX = 50;
        var microOriginY = viewportWidth <= 360 ? 34 : 32;
        var microScale = uniformMicroScale;
        var microRings = viewportWidth <= 360 ? 4 : 3;
        var microSlotsPerRing = Math.max(5, Math.ceil(ingredients.length / microRings) + 1);

        for (var microRing = 0; microRing < microRings; microRing += 1) {
          var microRadius = (viewportWidth <= 360 ? 28 : 26) + (microRing * (viewportWidth <= 360 ? 8 : 7));
          for (var microSlot = 0; microSlot < microSlotsPerRing; microSlot += 1) {
            var microAngle = -92 + ((360 / microSlotsPerRing) * microSlot) + (microRing * 11) + (ingredientIndex * 4.5);
            var microRadians = microAngle * Math.PI / 180;
            var microPoint = {
              xPercent: clampedPercent(microOriginX + Math.cos(microRadians) * microRadius),
              yPercent: clampedPercent(microOriginY + Math.sin(microRadians) * microRadius * .68),
              rotate: (microAngle * .12) + (jitterEnabled ? ((random() - .5) * 5) : 0),
              depth: Math.max(.1, .2 - microRing * .03),
              blur: Math.min(.52, .28 + microRing * .05),
              angle: microAngle,
              radius: microRadius,
              originX: microOriginX,
              originY: microOriginY
            };
            var microPlacement = tryCandidate(microPoint, microScale, true, true);
            if (microPlacement) return microPlacement;
          }
        }

        var perimeterSlots = viewportWidth <= 360
          ? [
            { x: 9, y: 13 }, { x: 91, y: 13 }, { x: 9, y: 87 }, { x: 91, y: 87 },
            { x: 50, y: 8 }, { x: 50, y: 92 }, { x: 16, y: 22 }, { x: 84, y: 22 },
            { x: 16, y: 74 }, { x: 84, y: 74 }, { x: 28, y: 11 }, { x: 72, y: 11 },
            { x: 28, y: 84 }, { x: 72, y: 84 }, { x: 6, y: 50 }, { x: 94, y: 50 }
          ]
          : [
            { x: 10, y: 14 }, { x: 90, y: 14 }, { x: 10, y: 86 }, { x: 90, y: 86 },
            { x: 50, y: 9 }, { x: 50, y: 91 }, { x: 20, y: 24 }, { x: 80, y: 24 },
            { x: 20, y: 72 }, { x: 80, y: 72 }, { x: 8, y: 50 }, { x: 92, y: 50 }
          ];

        for (var perimeterIndex = 0; perimeterIndex < perimeterSlots.length; perimeterIndex += 1) {
          var perimeterSlot = perimeterSlots[(perimeterIndex + ingredientIndex) % perimeterSlots.length];
          var perimeterPoint = {
            xPercent: perimeterSlot.x,
            yPercent: perimeterSlot.y,
            rotate: (perimeterIndex % 2 === 0 ? -6 : 6),
            depth: .12,
            blur: .42,
            angle: 0,
            radius: 0,
            originX: perimeterSlot.x,
            originY: perimeterSlot.y
          };
          var perimeterPlacement = tryCandidate(perimeterPoint, microScale, true, true);
          if (perimeterPlacement) return perimeterPlacement;
        }
      }

      return null;
    }

    function applyMobileMicroRing(ingredientList) {
      var originX = 50;
      var originY = viewportWidth <= 360 ? 34 : portraitViewport ? 32 : 30;
      var ringCount = ingredientList.length;
      var microScaleFactors = [1, .95, .9, .85, .8, .75, .7, .65, .6, .55, .5, .45, .4, .35, .3, .26, .22];
      var radii = [24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 33, 35, 37, 39, 41];
      var microGap = viewportWidth <= 320
        ? Math.max(8, ingredientGap - 4)
        : viewportWidth <= 480
          ? Math.max(9, ingredientGap - 3)
          : Math.max(10, ingredientGap - 2);
      var perimeterSlots = [
        { x: 8, y: 12 }, { x: 92, y: 12 }, { x: 8, y: 88 }, { x: 92, y: 88 },
        { x: 50, y: 7 }, { x: 50, y: 93 }, { x: 14, y: 20 }, { x: 86, y: 20 },
        { x: 14, y: 76 }, { x: 86, y: 76 }, { x: 26, y: 10 }, { x: 74, y: 10 },
        { x: 5, y: 48 }, { x: 95, y: 48 }, { x: 26, y: 86 }, { x: 74, y: 86 },
        { x: 20, y: 8 }, { x: 80, y: 8 }, { x: 20, y: 90 }, { x: 80, y: 90 }
      ];

      ingredientList.forEach(function (ingredient, ingredientIndex) {
        if (ingredient.classList.contains('is-placed')) return;
        var blockSeed = getIngredientBlockId(ingredient) || String(ingredientIndex);
        var random = seededRandom(hashString(seedBase + ':micro:' + blockSeed));
        var width = ingredient.offsetWidth || parseFloat(getComputedStyle(ingredient).width) || 88;
        var height = ingredient.offsetHeight || width;
        var zone = zones[ingredientIndex % zones.length];
        var placed = false;

        var baseAngle = -94 + ((360 / ringCount) * ingredientIndex);
        var baseRadius = radii[ingredientIndex % radii.length];

        for (var scaleIndex = 0; scaleIndex < microScaleFactors.length && !placed; scaleIndex += 1) {
          var microScale = uniformMicroScale * microScaleFactors[scaleIndex];

          for (var attempt = 0; attempt < 36 && !placed; attempt += 1) {
            var angle = baseAngle + (attempt * 5.5);
            var radius = baseRadius + (attempt % 6) * 2;
            var radians = angle * Math.PI / 180;
            var ringPoint = {
              xPercent: clampedPercent(originX + Math.cos(radians) * radius),
              yPercent: clampedPercent(originY + Math.sin(radians) * radius * .66),
              rotate: (angle * .1) + (jitterEnabled ? ((random() - .5) * 4) : 0),
              depth: zone.depth,
              blur: zone.blur,
              angle: angle,
              radius: radius,
              originX: originX,
              originY: originY
            };
            var ringBox = buildVisualBox(ringPoint, width, height, microScale);
            if (isValidPlacement(ringBox, microGap)) {
              applyPlacement(ingredient, ringBox, ingredientIndex, random);
              ingredient.classList.add('is-compact', 'is-micro');
              placed = true;
            }
          }

          for (var perimeterIndex = 0; perimeterIndex < perimeterSlots.length && !placed; perimeterIndex += 1) {
            var perimeterSlot = perimeterSlots[(perimeterIndex + ingredientIndex) % perimeterSlots.length];
            var perimeterPoint = {
              xPercent: perimeterSlot.x,
              yPercent: perimeterSlot.y,
              rotate: (perimeterIndex % 2 === 0 ? -5 : 5),
              depth: .12,
              blur: .36,
              angle: 0,
              radius: 0,
              originX: perimeterSlot.x,
              originY: perimeterSlot.y
            };
            var perimeterBox = buildVisualBox(perimeterPoint, width, height, microScale);
            if (isValidPlacement(perimeterBox, microGap)) {
              applyPlacement(ingredient, perimeterBox, ingredientIndex, random);
              ingredient.classList.add('is-compact', 'is-micro');
              placed = true;
            }
          }

          for (var sweepY = 6; sweepY <= 94 && !placed; sweepY += 3) {
            for (var sweepX = 6; sweepX <= 94; sweepX += 3) {
              var sweepPoint = {
                xPercent: sweepX,
                yPercent: sweepY,
                rotate: 0,
                depth: .1,
                blur: .34,
                angle: 0,
                radius: 0,
                originX: sweepX,
                originY: sweepY
              };
              var sweepBox = buildVisualBox(sweepPoint, width, height, microScale);
              if (isValidPlacement(sweepBox, microGap)) {
                applyPlacement(ingredient, sweepBox, ingredientIndex, random);
                ingredient.classList.add('is-compact', 'is-micro');
                placed = true;
                break;
              }
            }
          }
        }

        if (!placed && window.Shopify && window.Shopify.designMode) {
          console.warn('Morganics hero: micro-ring placement failed on slide "' + (slide.dataset.slideHandle || slide.dataset.slideIndex || '') + '". Block ID:', getIngredientBlockId(ingredient));
        }
      });
    }

    function placeRemainingIngredients(ingredientList, fallbackScale, gapOverride) {
      var remaining = ingredientList.filter(function (ingredient) {
        return !ingredient.classList.contains('is-placed');
      });
      if (!remaining.length) return;

      var fallbackGap = gapOverride == null ? Math.max(6, ingredientGap - 4) : gapOverride;
      var scaleStart = fallbackScale || (viewportWidth <= 480 ? uniformMicroScale : uniformScale * .72);
      var scaleFactors = [];
      for (var scaleStep = 0; scaleStep < 24; scaleStep += 1) {
        scaleFactors.push(scaleStart * (1 - scaleStep * .035));
      }

      remaining.forEach(function (ingredient, ingredientIndex) {
        var blockSeed = getIngredientBlockId(ingredient) || String(ingredientIndex);
        var random = seededRandom(hashString(seedBase + ':fallback:' + blockSeed));
        var width = ingredient.offsetWidth || parseFloat(getComputedStyle(ingredient).width) || 88;
        var height = ingredient.offsetHeight || width;
        var zone = zones[ingredientIndex % zones.length];
        var placed = false;
        var sweepStep = fallbackGap <= 5 ? 2 : 3;

        for (var scaleIndex = 0; scaleIndex < scaleFactors.length && !placed; scaleIndex += 1) {
          var scale = scaleFactors[scaleIndex];
          if (scale < .06) break;

          for (var sweepY = 4; sweepY <= 93 && !placed; sweepY += sweepStep) {
            for (var sweepX = 4; sweepX <= 93; sweepX += sweepStep) {
              var sweepPoint = {
                xPercent: sweepX,
                yPercent: sweepY,
                rotate: zone.rotate * .35,
                depth: zone.depth,
                blur: zone.blur,
                angle: 0,
                radius: 0,
                originX: sweepX,
                originY: sweepY
              };
              var sweepBox = buildVisualBox(sweepPoint, width, height, scale);
              if (isValidPlacement(sweepBox, fallbackGap)) {
                applyPlacement(ingredient, sweepBox, ingredientIndex, random);
                ingredient.classList.add('is-compact');
                if (viewportWidth <= 767) ingredient.classList.add('is-micro');
                placed = true;
              }
            }
          }
        }

        if (!placed && window.Shopify && window.Shopify.designMode) {
          console.warn('Morganics hero: fallback placement failed on slide "' + (slide.dataset.slideHandle || slide.dataset.slideIndex || '') + '". Block ID:', getIngredientBlockId(ingredient));
        }
      });
    }

    function normalizePlacedScales(ingredientList, fallbackScale) {
      var placedIngredients = ingredientList.filter(function (ingredient) {
        return ingredient.classList.contains('is-placed');
      });
      if (!placedIngredients.length) return;

      var floorScale = getIngredientVisibleScaleFloor(viewportWidth, scaleMultiplier);
      var minPlaced = placedIngredients.reduce(function (min, ingredient) {
        var value = parseFloat(ingredient.style.getPropertyValue('--scale') || fallbackScale);
        return Math.min(min, value);
      }, fallbackScale);
      var boostedScale = minPlaced * (viewportWidth <= 767 ? 1.55 : 1.24);
      var normalizedScale = Math.max(floorScale, Math.min(fallbackScale, boostedScale));

      placedIngredients.forEach(function (ingredient) {
        ingredient.style.setProperty('--scale', normalizedScale.toFixed(2));
        ingredient.style.setProperty('--ingredient-scale', normalizedScale.toFixed(2));
      });
    }

    function boxFromPlacedIngredient(ingredient, scale) {
      var width = ingredient.offsetWidth || parseFloat(getComputedStyle(ingredient).width) || 88;
      var height = ingredient.offsetHeight || width;
      var x = parseFloat(ingredient.style.getPropertyValue('--x') || '0');
      var y = parseFloat(ingredient.style.getPropertyValue('--y') || '0');
      var rotate = parseFloat(ingredient.style.getPropertyValue('--rotate') || '0');
      var blurPx = parseFloat(ingredient.style.getPropertyValue('--depth-blur') || '0');
      var scaledW = width * scale;
      var scaledH = height * scale;
      var positionLeft = x + ((width - scaledW) / 2);
      var positionTop = y + ((height - scaledH) / 2);
      var positionRight = positionLeft + scaledW;
      var positionBottom = positionTop + scaledH;
      var extents = getRotatedHalfExtents(width, height, scale, rotate);
      var motionPad = viewportWidth <= 480
        ? Math.max(2, (animationPadding * .22) + (blurPx * .45))
        : ((animationPadding * .65) + Math.max(3, blurPx * 1.1));
      var centerX = x + (width / 2);
      var centerY = y + (height / 2);
      return {
        x: x,
        y: y,
        positionLeft: positionLeft,
        positionTop: positionTop,
        positionRight: positionRight,
        positionBottom: positionBottom,
        left: centerX - extents.halfW - motionPad,
        top: centerY - extents.halfH - motionPad,
        right: centerX + extents.halfW + motionPad,
        bottom: centerY + extents.halfH + motionPad,
        scale: scale,
        rotate: rotate
      };
    }

    function boostPlacedScalesIfSafe(ingredientList, boostFactor) {
      if (viewportWidth < 1025 || boostFactor <= 1) return;
      var placedIngredients = ingredientList.filter(function (ingredient) {
        return ingredient.classList.contains('is-placed');
      });
      if (placedIngredients.length < 2) return;

      var currentScale = parseFloat(placedIngredients[0].style.getPropertyValue('--scale') || uniformScale);
      var boostedScale = currentScale * boostFactor;
      var testBoxes = placedIngredients.map(function (ingredient) {
        return boxFromPlacedIngredient(ingredient, boostedScale);
      });

      for (var i = 0; i < testBoxes.length; i += 1) {
        if (overlapsForbidden(testBoxes[i])) return;
        for (var j = i + 1; j < testBoxes.length; j += 1) {
          if (boxesTouch(testBoxes[i], testBoxes[j], ingredientGap)) return;
        }
      }

      placedIngredients.forEach(function (ingredient) {
        ingredient.style.setProperty('--scale', boostedScale.toFixed(2));
        ingredient.style.setProperty('--ingredient-scale', boostedScale.toFixed(2));
      });
      placedBoxes.length = 0;
      testBoxes.forEach(function (box) {
        placedBoxes.push(box);
      });
    }

    var placedBoxes = [];
    var usePortraitMicroRing = ingredients.length >= 4 && (
      viewportWidth <= 360 ||
      (viewportWidth <= 767 && portraitViewport)
    );

    if (usePortraitMicroRing) {
      applyMobileMicroRing(ingredients);
      placeRemainingIngredients(ingredients, uniformMicroScale * .84);
      placeRemainingIngredients(ingredients, uniformMicroScale * .68, Math.max(6, ingredientGap - 4));
      placeRemainingIngredients(ingredients, uniformMicroScale * .54, 4);
      placeRemainingIngredients(ingredients, uniformMicroScale * .38, 3);
      normalizePlacedScales(ingredients, uniformMicroScale);
      return;
    }

    ingredients.forEach(function (ingredient, ingredientIndex) {
      var blockSeed = getIngredientBlockId(ingredient) || String(ingredientIndex);
      var random = seededRandom(hashString(seedBase + ':' + blockSeed));
      var width = ingredient.offsetWidth || parseFloat(getComputedStyle(ingredient).width) || 88;
      var height = ingredient.offsetHeight || width;
      var zone = zones[ingredientIndex % zones.length];
      var point = scatterFromOrigin(ingredientIndex, random, zone);
      var targetScale = uniformScale;
      point.rotate = point.rotate + (jitterEnabled ? ((random() - .5) * 20) : 0);

      var placement = findPlacement(point, width, height, targetScale, random, ingredientIndex);
      if (!placement) {
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('Morganics hero: could not place ingredient without violating spacing rules on slide "' + (slide.dataset.slideHandle || slide.dataset.slideIndex || '') + '". Block ID:', getIngredientBlockId(ingredient));
        }
        return;
      }

      if (placement.compact) ingredient.classList.add('is-compact');
      if (placement.micro) ingredient.classList.add('is-micro');
      applyPlacement(ingredient, placement.box, ingredientIndex, random);
    });

    placeRemainingIngredients(ingredients, uniformScale * .78);
    placeRemainingIngredients(ingredients, uniformScale * .68, Math.max(6, ingredientGap - 6));
    normalizePlacedScales(ingredients, uniformScale);
    boostPlacedScalesIfSafe(ingredients, desktopScaleBoost);

    if (window.Shopify && window.Shopify.designMode) {
      var compactCount = slide.querySelectorAll('[data-mhs-ingredient].is-compact:not(.is-duplicate-config)').length;
      if (compactCount > 0) {
        console.info('Morganics hero: ' + compactCount + ' ingredient(s) used compact placement on ' + viewportTier + ' to preserve spacing rules.');
      }
    }
  };

  MorganicsHeroSlider.prototype.openSpotlight = function (ingredient) {
    var spotlight = this.root.querySelector('[data-mhs-spotlight]');
    if (!spotlight) return;
    var meta = readIngredientMeta(ingredient);
    var image = spotlight.querySelector('[data-spotlight-image]');
    var title = spotlight.querySelector('[data-spotlight-title]');
    var description = spotlight.querySelector('[data-spotlight-description]');
    var link = spotlight.querySelector('[data-spotlight-link]');

    image.src = meta.image;
    image.alt = meta.name;
    title.textContent = meta.name;
    if (description) description.textContent = meta.description;
    link.href = meta.productUrl;
    link.textContent = meta.ctaLabel;
    link.hidden = false;
    spotlight.classList.add('is-visible');
    spotlight.setAttribute('aria-hidden', 'false');
    this.spotlightOpen = true;
    this.pause();
  };

  MorganicsHeroSlider.prototype.closeSpotlight = function () {
    var spotlight = this.root.querySelector('[data-mhs-spotlight]');
    if (!spotlight) return;
    spotlight.classList.remove('is-visible');
    spotlight.setAttribute('aria-hidden', 'true');
    this.spotlightOpen = false;
    this.resume();
  };

  MorganicsHeroSlider.prototype.getDuration = function () {
    var slideConfig = this.config.slides && this.config.slides[this.currentIndex];
    var duration = slideConfig && Number(slideConfig.duration);
    return duration > 0 ? duration : Number(this.config.defaultDuration || 7000);
  };

  MorganicsHeroSlider.prototype.startAutoplay = function () {
    var self = this;
    window.clearTimeout(this.timer);
    if (!this.config.autoplay || this.slides.length < 2 || this.paused || this.spotlightOpen || this.prefersReducedMotion || this.config.reduceMotion) return;
    this.timer = window.setTimeout(function () {
      self.activate(self.currentIndex + 1, false);
    }, this.getDuration());
  };

  MorganicsHeroSlider.prototype.restartAutoplay = function () {
    window.clearTimeout(this.timer);
    this.startAutoplay();
  };

  MorganicsHeroSlider.prototype.pause = function () {
    this.paused = true;
    window.clearTimeout(this.timer);
  };

  MorganicsHeroSlider.prototype.resume = function () {
    if (this.spotlightOpen || document.hidden) return;
    this.paused = false;
    this.startAutoplay();
  };

  MorganicsHeroSlider.prototype.destroy = function () {
    window.clearTimeout(this.timer);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.destroyController.abort();
    instances.delete(this.root);
  };

  function initialize(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new MorganicsHeroSlider(root));
  }

  function initializeAll(scope) {
    var parent = scope || document;
    if (parent.matches && parent.matches('[data-morganics-hero-slider]')) initialize(parent);
    parent.querySelectorAll('[data-morganics-hero-slider]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    }, { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.matches('[data-morganics-hero-slider]') ? event.target : event.target.querySelector('[data-morganics-hero-slider]');
    var instance = root && instances.get(root);
    if (instance) instance.destroy();
  });

  window.MorganicsHeroSlider = MorganicsHeroSlider;
  MorganicsHeroSlider.readIngredientMeta = readIngredientMeta;
})();
(() => {
  const desktopQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)');
  const ingredientSelector = '.mhs-ingredient, [data-mhs-ingredient], .morganics-hero-ingredient';
  const heroSelector = '[data-morganics-hero], .morganics-hero, .mhs-hero, .mhs-section, .shopify-section';
  let activeCard = null;
  let activeRoot = null;
  let activeIngredient = null;

  const getConfig = () => window.MorganicsHeroIngredientConfig || {};

  const findHeroRoot = (ingredient) => {
    return ingredient.closest('[data-morganics-hero-slider], .morganics-hero, .mhs-hero, .mhs-section') || document.body;
  };

  const readMeta = (ingredient) => {
    if (window.MorganicsHeroSlider && typeof window.MorganicsHeroSlider.readIngredientMeta === 'function') {
      return window.MorganicsHeroSlider.readIngredientMeta(ingredient);
    }
    const imgEl = ingredient.querySelector('img');
    const image = ingredient.dataset.ingredientImage || imgEl?.currentSrc || imgEl?.src || '';
    const name = ingredient.dataset.title || ingredient.getAttribute('aria-label')?.replace(/^View\s+/i, '') || 'Featured ingredient';
    const description = ingredient.dataset.description || 'Discover premium Morganics pantry essentials.';
    const productUrl = ingredient.dataset.productUrl || ingredient.dataset.link || '/collections/all';
    return { image, name, description, productUrl, ctaLabel: 'View product', hasLinkedProduct: false };
  };

  const buildCard = () => {
    const card = document.createElement('aside');
    card.className = 'mhs-ingredient-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'false');
    card.innerHTML = `
      <button class="mhs-ingredient-card__close" type="button" aria-label="Close ingredient details">&times;</button>
      <div class="mhs-ingredient-card__media"><img alt="" loading="lazy"></div>
      <div class="mhs-ingredient-card__body">
        <h3 class="mhs-ingredient-card__title"></h3>
        <p class="mhs-ingredient-card__description"></p>
        <a class="mhs-ingredient-card__cta" href="/collections/all">View Product</a>
      </div>
    `;
    document.body.appendChild(card);
    card.querySelector('.mhs-ingredient-card__close').addEventListener('click', closeCard);
    return card;
  };

  const positionCard = (card, ingredient) => {
    const rect = ingredient.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const gap = 18;
    const margin = 16;
    let left = rect.right + gap;
    let top = rect.top + rect.height / 2 - cardRect.height / 2;

    if (left + cardRect.width + margin > window.innerWidth) {
      left = rect.left - cardRect.width - gap;
    }
    if (left < margin) {
      left = Math.min(window.innerWidth - cardRect.width - margin, Math.max(margin, rect.left));
      top = rect.bottom + gap;
    }
    if (top + cardRect.height + margin > window.innerHeight) {
      top = window.innerHeight - cardRect.height - margin;
    }
    if (top < margin) top = margin;

    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
  };

  function closeCard() {
    if (activeCard) activeCard.classList.remove('is-visible');
    if (activeRoot) activeRoot.classList.remove('mhs-ingredient-focus');
    if (activeIngredient) activeIngredient.classList.remove('is-ingredient-selected');
    activeRoot = null;
    activeIngredient = null;
  }

  const openCard = (ingredient) => {
    if (!desktopQuery.matches) return;
    const root = findHeroRoot(ingredient);
    const meta = readMeta(ingredient);
    const card = activeCard || buildCard();
    activeCard = card;

    if (activeIngredient && activeIngredient !== ingredient) {
      activeIngredient.classList.remove('is-ingredient-selected');
    }
    if (activeRoot && activeRoot !== root) {
      activeRoot.classList.remove('mhs-ingredient-focus');
    }

    card.querySelector('.mhs-ingredient-card__media img').src = meta.image;
    card.querySelector('.mhs-ingredient-card__media img').alt = meta.name;
    card.querySelector('.mhs-ingredient-card__title').textContent = meta.name;
    card.querySelector('.mhs-ingredient-card__description').textContent = meta.description;
    const cta = card.querySelector('.mhs-ingredient-card__cta');
    cta.href = meta.productUrl;
    cta.textContent = meta.ctaLabel || 'View product';

    activeRoot = root;
    activeIngredient = ingredient;
    root.classList.add('mhs-ingredient-focus');
    ingredient.classList.add('is-ingredient-selected');

    card.classList.add('is-visible');
    positionCard(card, ingredient);
  };

  const hydrateIngredient = (ingredient) => {
    if (ingredient.dataset.ingredientHydrated === 'true') return;
    ingredient.dataset.ingredientHydrated = 'true';
    if (!ingredient.hasAttribute('tabindex')) ingredient.setAttribute('tabindex', '0');
    if (!ingredient.getAttribute('role')) ingredient.setAttribute('role', 'button');
    ingredient.addEventListener('click', (event) => {
      if (!desktopQuery.matches) return;
      event.preventDefault();
      event.stopPropagation();
      openCard(ingredient);
    });
    ingredient.addEventListener('keydown', (event) => {
      if (!desktopQuery.matches) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openCard(ingredient);
      }
    });
  };

  const hydratedRoots = new WeakSet();

  const init = (scope) => {
    const parent = scope || document;
    const roots = parent.matches && parent.matches('[data-morganics-hero-slider], .morganics-hero, .mhs-hero, .mhs-section')
      ? [parent]
      : Array.from(parent.querySelectorAll('[data-morganics-hero-slider], .morganics-hero, .mhs-hero, .mhs-section'));
    roots.forEach((root) => {
      if (hydratedRoots.has(root)) return;
      hydratedRoots.add(root);
      root.querySelectorAll(ingredientSelector).forEach(hydrateIngredient);
    });
  };

  document.addEventListener('click', (event) => {
    if (!activeCard) return;
    if (activeCard.contains(event.target) || activeIngredient?.contains(event.target)) return;
    closeCard();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCard();
  });

  window.addEventListener('resize', () => {
    if (!desktopQuery.matches) {
      closeCard();
      return;
    }
    if (activeCard && activeIngredient) positionCard(activeCard, activeIngredient);
  }, { passive: true });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    closeCard();
    const root = event.target.matches && event.target.matches(heroSelector)
      ? event.target
      : event.target.querySelector && event.target.querySelector(heroSelector);
    if (root) hydratedRoots.delete(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
